import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Clock } from "lucide-react";

const CHECKOUT_DISABLED = process.env.REACT_APP_CHECKOUT_DISABLED === "true";

const Cart = () => {
  const { items, updateQty, removeItem, subtotal, shipping, total, clearCart } = useCart();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <ShoppingBag className="h-14 w-14 text-gray-300 mx-auto mb-4" />
        <h1 className="font-display text-4xl font-bold text-[#0a0a0a]">Your cart is empty</h1>
        <p className="text-gray-600 mt-2">Start shopping our research peptides.</p>
        <Button asChild className="btn-red mt-6 h-12 px-8 font-bold uppercase tracking-wide">
          <Link to="/shop">Shop Now</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
      <h1 className="font-display text-4xl md:text-5xl font-bold text-[#0a0a0a] uppercase">Shopping Cart</h1>
      <div className="stripe-bar mt-3 mb-8" />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_40px] px-5 py-3 border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500">
            <div>Product</div><div>Price</div><div>Qty</div><div>Total</div><div></div>
          </div>
          {items.map((i) => (
            <div key={i.key} className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_40px] gap-3 items-center px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <img src={i.image} alt={i.name} className="h-20 w-20 rounded border border-gray-200 object-contain p-1 bg-white" />
                <div>
                  <div className="font-semibold text-[#0a0a0a]">{i.name}</div>
                  <div className="text-xs text-gray-500">{i.size}</div>
                </div>
              </div>
              <div className="text-[#0a0a0a] font-semibold">${i.price.toFixed(2)}</div>
              <div className="inline-flex items-center border border-gray-300 rounded w-fit">
                <button className="px-2 py-1 hover:bg-gray-100" onClick={() => updateQty(i.key, i.qty - 1)}><Minus className="h-3 w-3" /></button>
                <span className="px-3 text-sm font-semibold">{i.qty}</span>
                <button className="px-2 py-1 hover:bg-gray-100" onClick={() => updateQty(i.key, i.qty + 1)}><Plus className="h-3 w-3" /></button>
              </div>
              <div className="font-bold text-[#0a0a0a]">${(i.price * i.qty).toFixed(2)}</div>
              <button onClick={() => removeItem(i.key)} className="text-gray-400 hover:text-[#b91c1c]"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <div className="px-5 py-4 flex justify-between">
            <Button asChild variant="outline"><Link to="/shop">Continue Shopping</Link></Button>
            <Button variant="ghost" className="text-[#b91c1c]" onClick={clearCart}>Clear Cart</Button>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 h-fit sticky top-32">
          <h2 className="font-display text-xl font-bold text-[#0a0a0a] mb-4">Order Summary</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span className="font-semibold">${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span className="font-semibold">{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span></div>
            {subtotal > 0 && subtotal < 200 && (<div className="text-xs text-[#b91c1c]">Add ${(200 - subtotal).toFixed(2)} more for FREE shipping!</div>)}
            <div className="flex justify-between text-lg font-bold pt-3 border-t border-gray-200 mt-3"><span>Total</span><span className="text-[#b91c1c]">${total.toFixed(2)}</span></div>
          </div>
          <Button asChild className="btn-red w-full mt-5 h-12 font-bold uppercase tracking-wide">
            <Link to="/checkout" data-testid="proceed-to-checkout-link">
              {CHECKOUT_DISABLED ? "Join Launch List" : "Proceed to Checkout"} <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
          {CHECKOUT_DISABLED ? (
            <div className="mt-3 flex items-start gap-2 text-[11px] text-gray-500 font-light" data-testid="cart-prelaunch-notice">
              <Clock className="h-3.5 w-3.5 text-[#b91c1c] flex-shrink-0 mt-0.5" />
              <span>Online ordering opens shortly. Save your cart and join the launch list for an exclusive promo code.</span>
            </div>
          ) : (
            <p className="text-xs text-center text-gray-500 mt-3">Secure checkout • Encrypted</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;
